import { create } from 'zustand';
import {
  BaseOrder,
  MedicationOrder,
  ImagingOrder,
  OrderSet,
  OrderStatus,
  DrugSearchResult,
  OrderAction,
  DrugInteraction,
  AllergyAlert,
  DosingAlert,
  DuplicateTherapyAlert,
  ContraindicationAlert
} from '../types/cpoe';
import { LabOrder } from '../types/labOrder';
import apiClient from '../services/apiClient';

interface CPOEStore {
  // Orders
  orders: Map<string, BaseOrder | MedicationOrder | ImagingOrder | LabOrder>;
  
  // Order Sets
  orderSets: OrderSet[];
  personalOrderSets: OrderSet[];
  
  // Drug Database Cache
  drugSearchCache: Map<string, DrugSearchResult[]>;
  
  // Clinical Decision Support
  activeAlerts: {
    interactions: DrugInteraction[];
    allergies: AllergyAlert[];
    dosing: DosingAlert[];
    contraindications: ContraindicationAlert[];
    duplicateTherapy: DuplicateTherapyAlert[];
  };
  
  // Actions - Orders
  addOrder: (order: BaseOrder | MedicationOrder | ImagingOrder | LabOrder) => void;
  updateOrder: (orderId: string, updates: Partial<BaseOrder>) => void;
  deleteOrder: (orderId: string) => void;
  getOrder: (orderId: string) => BaseOrder | MedicationOrder | ImagingOrder | LabOrder | undefined;
  getOrdersByPatient: (patientId: string) => (BaseOrder | MedicationOrder | ImagingOrder | LabOrder)[];
  getActiveOrdersByPatient: (patientId: string) => (BaseOrder | MedicationOrder | ImagingOrder | LabOrder)[];
  
  // Actions - Order Lifecycle
  submitOrder: (orderId: string) => Promise<void>;
  holdOrder: (orderId: string, reason: string) => void;
  resumeOrder: (orderId: string) => void;
  discontinueOrder: (orderId: string, reason: string, discontinuedBy: { id: string; name: string; title: string }) => void;
  renewOrder: (orderId: string, changes?: Partial<BaseOrder>) => string;
  
  // Actions - Order Sets
  loadOrderSets: (category?: string, specialty?: string) => Promise<void>;
  createPersonalOrderSet: (orderSet: Omit<OrderSet, 'id' | 'version' | 'effectiveDate'>) => void;
  applyOrderSet: (orderSetId: string, patientId: string) => string[]; // Returns array of created order IDs
  
  // Actions - Drug Search
  searchDrugs: (query: string) => Promise<DrugSearchResult[]>;
  getDrugDetails: (rxcui: string) => Promise<DrugSearchResult | null>;
  
  // Actions - Clinical Decision Support
  checkDrugInteractions: (medications: MedicationOrder[]) => Promise<DrugInteraction[]>;
  checkDrugAllergies: (medication: MedicationOrder, patientId: string) => Promise<AllergyAlert[]>;
  checkDosing: (medication: MedicationOrder, patientId: string) => Promise<DosingAlert[]>;
  checkDuplicateTherapy: (medication: MedicationOrder, existingMedications: MedicationOrder[]) => DuplicateTherapyAlert[];
  performAllSafetyChecks: (order: MedicationOrder, patientId: string) => Promise<void>;
  
  // Actions - Imaging Safety
  checkImagingSafety: (order: ImagingOrder, patientId: string) => Promise<{
    cleared: boolean;
    alerts: string[];
  }>;
  
  // Actions - Order History
  getOrderHistory: (orderId: string) => OrderAction[];
  
  // Clear alerts
  clearAlerts: () => void;
}

const useCPOEStore = create<CPOEStore>((set, get) => ({
  orders: new Map(),
  orderSets: [],
  personalOrderSets: [],
  drugSearchCache: new Map(),
  activeAlerts: {
    interactions: [],
    allergies: [],
    dosing: [],
    contraindications: [],
    duplicateTherapy: []
  },

  // Order Management
  addOrder: (order) => {
    set((state) => {
      const newOrders = new Map(state.orders);
      newOrders.set(order.id, order);
      return { orders: newOrders };
    });
  },

  updateOrder: (orderId, updates) => {
    set((state) => {
      const newOrders = new Map(state.orders);
      const order = newOrders.get(orderId);
      if (order) {
        newOrders.set(orderId, { ...order, ...updates } as typeof order);
      }
      return { orders: newOrders };
    });
  },

  deleteOrder: (orderId) => {
    set((state) => {
      const newOrders = new Map(state.orders);
      newOrders.delete(orderId);
      return { orders: newOrders };
    });
  },

  getOrder: (orderId) => {
    return get().orders.get(orderId);
  },

  getOrdersByPatient: (patientId) => {
    return Array.from(get().orders.values()).filter(order => order.patientId === patientId);
  },

  getActiveOrdersByPatient: (patientId) => {
    return Array.from(get().orders.values()).filter(
      order => order.patientId === patientId && 
      ['active', 'pending', 'on-hold'].includes(order.status)
    );
  },

  // Order Lifecycle
  submitOrder: async (orderId) => {
    const order = get().getOrder(orderId);
    if (!order || order.status !== 'draft') return;

    // Perform safety checks for medication orders
    if ((order as BaseOrder).type === 'medication') {
      await get().performAllSafetyChecks(order as MedicationOrder, order.patientId);
    }

    // Update order status
    get().updateOrder(orderId, { 
      status: 'pending',
      orderedAt: new Date()
    });

    // Simulate order processing
    setTimeout(() => {
      get().updateOrder(orderId, { status: 'active' });
    }, 2000);
  },

  holdOrder: (orderId, reason) => {
    get().updateOrder(orderId, { 
      status: 'on-hold',
      notes: { en: reason, ar: reason }
    });
  },

  resumeOrder: (orderId) => {
    get().updateOrder(orderId, { status: 'active' });
  },

  discontinueOrder: (orderId, reason, discontinuedBy) => {
    get().updateOrder(orderId, {
      status: 'discontinued',
      discontinuedBy,
      discontinuedAt: new Date(),
      discontinuedReason: { en: reason, ar: reason }
    });
  },

  renewOrder: (orderId, changes) => {
    const originalOrder = get().getOrder(orderId);
    if (!originalOrder) return '';

    const newOrderId = `order-${Date.now()}`;
    const newOrder = {
      ...originalOrder,
      ...changes,
      id: newOrderId,
      status: 'draft' as OrderStatus,
      orderedAt: new Date()
    };

    get().addOrder(newOrder as BaseOrder | MedicationOrder | ImagingOrder | LabOrder);
    return newOrderId;
  },

  // Order Sets
  loadOrderSets: async (category, specialty) => {
    // Load comprehensive order set templates
    const { ORDER_SET_TEMPLATES, getOrderSetsByCategory, getOrderSetsBySpecialty } = await import('../data/orderSetTemplates');

    let orderSets = ORDER_SET_TEMPLATES;

    // Filter by category if specified
    if (category) {
      orderSets = getOrderSetsByCategory(category as 'admission' | 'post-operative' | 'disease-specific' | 'personal');
    }

    // Filter by specialty if specified
    if (specialty) {
      orderSets = getOrderSetsBySpecialty(specialty);
    }

    set({ orderSets });
  },

  createPersonalOrderSet: (orderSet) => {
    const newOrderSet: OrderSet = {
      ...orderSet,
      id: `pos-${Date.now()}`,
      version: 1,
      effectiveDate: new Date()
    };

    set((state) => ({
      personalOrderSets: [...state.personalOrderSets, newOrderSet]
    }));
  },

  applyOrderSet: (orderSetId, patientId) => {
    const orderSet = [...get().orderSets, ...get().personalOrderSets].find(os => os.id === orderSetId);
    if (!orderSet) return [];

    const createdOrderIds: string[] = [];

    orderSet.orders.forEach(item => {
      if (item.isPreselected || item.isRequired) {
        const orderId = `order-${Date.now()}-${Math.random()}`;
        const newOrder = {
          ...item.order,
          id: orderId,
          patientId,
          status: 'draft' as OrderStatus,
          orderedAt: new Date()
        };

        get().addOrder(newOrder as BaseOrder);
        createdOrderIds.push(orderId);
      }
    });

    return createdOrderIds;
  },

  // Drug Search - Using RxNorm API
  searchDrugs: async (query) => {
    // Check cache first
    const cached = get().drugSearchCache.get(query);
    if (cached) return cached;

    try {
      // Call RxNorm API through backend with authentication
      const response = await apiClient.get('/drugs/search', {
        params: { query, maxResults: 20 }
      });

      // Backend wraps response in { success: true, data: [...] }
      const rxnormResults = response.data?.data || response.data;

      // Transform RxNorm results to DrugSearchResult format
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const results: DrugSearchResult[] = rxnormResults.map((drug: any) => {
        // Extract strength and formulation from drug name
        const strengthMatch = drug.name.match(/(\d+(?:\.\d+)?\s*(?:MG|ML|MCG|GM|%|UNIT))/i);
        const strength = strengthMatch ? [strengthMatch[1]] : [];

        // Determine formulation from TTY and name
        const formulations: string[] = [];
        if (drug.name.toLowerCase().includes('tablet')) formulations.push('tablet');
        if (drug.name.toLowerCase().includes('capsule')) formulations.push('capsule');
        if (drug.name.toLowerCase().includes('suspension') || drug.name.toLowerCase().includes('solution')) formulations.push('liquid');
        if (drug.name.toLowerCase().includes('injection')) formulations.push('injection');
        if (formulations.length === 0) formulations.push('oral');

        return {
          rxcui: drug.rxcui,
          genericName: drug.name,
          brandNames: drug.synonym ? [drug.synonym] : [],
          strength,
          formulations,
          therapeuticClass: 'Unknown' // Would need additional API call to get class
        };
      });

      // Cache results
      set((state) => {
        const newCache = new Map(state.drugSearchCache);
        newCache.set(query, results);
        return { drugSearchCache: newCache };
      });

      return results;
    } catch (error) {
      console.error('Error searching drugs:', error);
      // Return empty array on error
      return [];
    }
  },

  getDrugDetails: async (rxcui) => {
    try {
      const response = await apiClient.get(`/drugs/${rxcui}`);
      // Backend wraps response in { success: true, data: {...} }
      const drug = response.data?.data || response.data;

      // Transform to DrugSearchResult format
      const strengthMatch = drug.name.match(/(\d+(?:\.\d+)?\s*(?:MG|ML|MCG|GM|%|UNIT))/i);
      const strength = strengthMatch ? [strengthMatch[1]] : [];

      const formulations: string[] = [];
      if (drug.name.toLowerCase().includes('tablet')) formulations.push('tablet');
      if (drug.name.toLowerCase().includes('capsule')) formulations.push('capsule');
      if (drug.name.toLowerCase().includes('suspension') || drug.name.toLowerCase().includes('solution')) formulations.push('liquid');
      if (drug.name.toLowerCase().includes('injection')) formulations.push('injection');
      if (formulations.length === 0) formulations.push('oral');

      return {
        rxcui: drug.rxcui,
        genericName: drug.name,
        brandNames: drug.synonym ? [drug.synonym] : [],
        strength,
        formulations,
        therapeuticClass: 'Unknown'
      };
    } catch (error) {
      console.error('Error getting drug details:', error);
      return null;
    }
  },

  // Clinical Decision Support
  checkDrugInteractions: async (medications) => {
    // Simulate interaction checking
    const interactions: DrugInteraction[] = [];
    
    // Example: Check for NSAID + Anticoagulant interaction
    const hasNSAID = medications.some(med => 
      med.medication.genericName.toLowerCase().includes('ibuprofen') ||
      med.medication.genericName.toLowerCase().includes('aspirin')
    );
    const hasAnticoagulant = medications.some(med => 
      med.medication.genericName.toLowerCase().includes('warfarin')
    );

    if (hasNSAID && hasAnticoagulant) {
      interactions.push({
        severity: 'severe',
        drug: 'Warfarin',
        description: { 
          en: 'NSAIDs may increase the risk of bleeding when used with anticoagulants',
          ar: 'قد تزيد مضادات الالتهاب غير الستيرويدية من خطر النزيف عند استخدامها مع مضادات التخثر'
        },
        recommendation: {
          en: 'Monitor INR closely or consider alternative analgesic',
          ar: 'راقب INR عن كثب أو فكر في مسكن بديل'
        }
      });
    }

    return interactions;
  },

  checkDrugAllergies: async (medication, patientId) => {
    // Import allergy utilities dynamically
    const { parsePatientAllergies, checkDrugAgainstAllergies } = await import('../utils/allergyIntegration');
    const { usePatientStore } = await import('./usePatientStore');
    const { medicalHistoryApi } = await import('../services/api/medicalHistoryApi');

    // Get patient data
    const patient = usePatientStore.getState().getPatient(patientId);
    if (!patient) {
      return [];
    }

    // Parse patient allergies from their basic record
    const basicAllergies = parsePatientAllergies(patient);

    // Also get allergies from the Medical History module
    let medicalHistoryAllergies: AllergyAlert[] = [];
    try {
      const medicalHistory = await medicalHistoryApi.getByPatientId(patientId);
      if (medicalHistory?.allergies) {
        // Convert Medical History allergies to AllergyAlert format
        medicalHistoryAllergies = medicalHistory.allergies
          .filter(a => a.isActive)
          .map(a => ({
            id: a.id,
            patientId: patientId,
            allergen: a.allergen,
            allergenType: a.type as 'drug' | 'food' | 'environmental',
            reaction: a.reaction,
            severity: a.severity as 'mild' | 'moderate' | 'severe' | 'life-threatening',
            notes: a.notes || `From Medical History (onset: ${a.onsetDate || 'unknown'})`,
          })) as unknown as AllergyAlert[];
      }
    } catch (_error) {
      // Medical history may not exist, that's OK - just use basic allergies
      console.debug('No medical history found for patient, using basic allergies only');
    }

    // Combine both sources of allergies (dedupe by allergen name)
    const allAllergies = [...basicAllergies];
    for (const mhAllergy of medicalHistoryAllergies) {
      const isDupe = allAllergies.some(
        a => a.allergen.toLowerCase() === mhAllergy.allergen.toLowerCase()
      );
      if (!isDupe) {
        allAllergies.push(mhAllergy as unknown as typeof allAllergies[number]);
      }
    }

    // Check if the medication matches any allergies
    const allergyMatches: AllergyAlert[] = [];
    for (const allergy of allAllergies) {
      const match = checkDrugAgainstAllergies(medication.medication.genericName, [allergy]);
      if (match) {
        allergyMatches.push({
          allergen: match.allergen,
          reaction: match.reaction,
          severity: match.severity as 'mild' | 'moderate' | 'severe',
          crossSensitivity: match.notes?.includes('Cross-reactivity') || false,
          notes: match.notes
        } as AllergyAlert);
      }
    }

    return allergyMatches;
  },

  checkDosing: async (medication, patientId) => {
    // Import dosage calculator and patient store
    const { calculateRecommendedDosage, getLatestPatientVitals, estimateCreatinineClearance, categorizeRenalFunction } = await import('../utils/dosageCalculator');
    const { usePatientStore } = await import('./usePatientStore');
    const { useMedicalRecordStore } = await import('./useMedicalRecordStore');

    // Get patient data
    const patient = usePatientStore.getState().getPatient(patientId);
    if (!patient) {
      return [];
    }

    // Get patient vitals from medical records
    const medicalRecords = useMedicalRecordStore.getState().medicalRecords;
    const vitals = getLatestPatientVitals(patient, medicalRecords);

    // Estimate renal function if weight is available
    let renalFunction: 'normal' | 'mild' | 'moderate' | 'severe' = 'normal';
    if (vitals?.weight && patient.age) {
      const crCl = estimateCreatinineClearance(
        patient.age,
        vitals.weight,
        patient.gender
      );
      renalFunction = categorizeRenalFunction(crCl);
    }

    // Calculate recommended dosage
    const recommendation = calculateRecommendedDosage({
      drugName: medication.medication.genericName,
      patientAge: patient.age,
      patientWeight: vitals?.weight,
      patientHeight: vitals?.height?.toString(),
      renalFunction,
      requestedDose: medication.dosage.amount,
      requestedUnit: medication.dosage.unit,
    });

    const alerts: DosingAlert[] = [];

    // Check if dose is outside safe range
    if (!recommendation.isWithinSafeRange) {
      alerts.push({
        type: 'dosage',
        message: {
          en: `Requested dose (${medication.dosage.amount}${medication.dosage.unit}) differs from recommended dose`,
          ar: `الجرعة المطلوبة (${medication.dosage.amount}${medication.dosage.unit}) تختلف عن الجرعة الموصى بها`,
        },
        recommendation: {
          en: `Recommended: ${recommendation.recommendedDose}${recommendation.unit} ${recommendation.frequency}`,
          ar: `موصى به: ${recommendation.recommendedDose}${recommendation.unit} ${recommendation.frequency}`,
        },
        adjustedDose: {
          amount: recommendation.recommendedDose,
          unit: recommendation.unit,
        },
      });
    }

    // Add warnings as alerts
    if (recommendation.warnings) {
      for (const warning of recommendation.warnings) {
        const isContraindicated = warning.en.includes('CONTRAINDICATED');
        alerts.push({
          type: isContraindicated ? 'contraindication' : renalFunction !== 'normal' ? 'renal' : 'age',
          message: warning,
          recommendation: recommendation.adjustmentReason || {
            en: `Use recommended dose: ${recommendation.recommendedDose}${recommendation.unit}`,
            ar: `استخدم الجرعة الموصى بها: ${recommendation.recommendedDose}${recommendation.unit}`,
          },
          adjustedDose: {
            amount: recommendation.recommendedDose,
            unit: recommendation.unit,
          },
        });
      }
    }

    return alerts;
  },

  checkDuplicateTherapy: (medication, existingMedications) => {
    const duplicates: DuplicateTherapyAlert[] = [];
    
    // Check for same therapeutic class
    existingMedications.forEach(existing => {
      if (existing.medication.therapeuticClass === medication.medication.therapeuticClass) {
        duplicates.push({
          existingMedication: existing.medication.genericName,
          therapeuticClass: medication.medication.therapeuticClass || 'Unknown',
          recommendation: {
            en: 'Consider if both medications are needed',
            ar: 'فكر فيما إذا كانت هناك حاجة لكلا الدوائين'
          }
        });
      }
    });

    return duplicates;
  },

  performAllSafetyChecks: async (order, patientId) => {
    const existingMeds = get().getActiveOrdersByPatient(patientId)
      .filter(o => (o as BaseOrder).type === 'medication') as MedicationOrder[];

    const [interactions, allergies, dosing] = await Promise.all([
      get().checkDrugInteractions([...existingMeds, order]),
      get().checkDrugAllergies(order, patientId),
      get().checkDosing(order, patientId)
    ]);

    const duplicateTherapy = get().checkDuplicateTherapy(order, existingMeds);

    set({
      activeAlerts: {
        interactions,
        allergies,
        dosing,
        contraindications: [],
        duplicateTherapy
      }
    });
  },

  // Imaging Safety
  checkImagingSafety: async (order, _patientId) => {
    const alerts: string[] = [];
    let cleared = true;

    // Check pregnancy status for radiation exposure
    if (order.pregnancyStatus === 'pregnant' && 
        ['ct', 'xray', 'nuclear', 'pet'].includes(order.modality)) {
      alerts.push('Patient is pregnant - radiation exposure risk');
      cleared = false;
    }

    // Check renal function for contrast
    if (order.contrastRequired && order.safetyScreening?.renalFunction?.gfr) {
      if (order.safetyScreening.renalFunction.gfr < 30) {
        alerts.push('Severe renal impairment - contrast contraindicated');
        cleared = false;
      } else if (order.safetyScreening.renalFunction.gfr < 60) {
        alerts.push('Moderate renal impairment - use contrast with caution');
      }
    }

    // Check MRI safety
    if (order.modality === 'mri') {
      if (order.safetyScreening?.pacemaker) {
        alerts.push('Pacemaker present - MRI contraindicated');
        cleared = false;
      }
      if (order.safetyScreening?.metalImplants) {
        alerts.push('Metal implants present - verify MRI compatibility');
      }
    }

    return { cleared, alerts };
  },

  // Order History
  getOrderHistory: (_orderId) => {
    // This would integrate with audit log
    return [];
  },

  clearAlerts: () => {
    set({
      activeAlerts: {
        interactions: [],
        allergies: [],
        dosing: [],
        contraindications: [],
        duplicateTherapy: []
      }
    });
  }
}));

export { useCPOEStore };